import type { PropInstance } from "../document/types.ts";
import { artHeight, artLeft, artTop, artWidth } from "./art-box.ts";
import type { FrameSize, PixelBox } from "./prop-hit.ts";

/**
 * Which props hide a character (ADR-040): a prop fades while its solid pixels hide most of a box standing for
 * the character's body. `propArtHeight` says how tall a prop is, `propArtOverlap` is the cheap test over the
 * art bounds and `propCoverage` the count over the pixels themselves. A prop is anchored as everywhere else:
 * its frame's bottom-left on the bottom-left of its anchor cell, and world pixel `p` shows frame pixel
 * `floor(p - origin)`, so a prop placed at a pixel inside its tile is measured where it is drawn. The game
 * decides the box and how much of it must be hidden.
 */

/**
 * A frame's solid pixels: `w` by `h` bytes, row-major from the frame's top-left and unflipped, 1 where the
 * pixel's alpha is at least `SOLID_ALPHA` (the pixels `base` and `art` are measured from) and 0 elsewhere.
 */
export interface FrameMask {
  readonly w: number;
  readonly h: number;
  readonly solid: Uint8Array;
}

/**
 * Looks up a frame's solid pixels by frame id. A lookup whose atlas holds no such frame throws
 * `MissingFrameError`; any other error means the atlas could not be read.
 */
export type FrameMaskOf = (frame: string) => FrameMask;

/**
 * Thrown by a `FrameMaskOf` for a frame id its atlas does not hold, so a caller can tell one absent frame from
 * an atlas that cannot be read.
 */
export class MissingFrameError extends Error {
  /**
   * @param frame The frame id.
   */
  public constructor(public readonly frame: string) {
    super(`asset manifest has no frame ${frame}`);
    this.name = "MissingFrameError";
  }
}

/**
 * Where a prop stands and how it is mirrored: all the geometry the tests below read from it.
 */
type Placement = Pick<PropInstance, "x" | "y" | "flipX" | "flipY">;

/**
 * The height of a frame's opaque art above its base: what "tall" means for a prop.
 *
 * @param size The frame.
 * @returns Rows from the first opaque row to the base, or the base itself without art bounds.
 */
export function propArtHeight(size: FrameSize): number {
  return size.art === undefined ? size.base : size.base - size.art.y;
}

/**
 * Whether a frame may be dimmed at all, by the occluder fade or the cone of sight: every frame but one the asset
 * manifest marks `noFade`, which the game chooses at the asset build (`DaedalusGameAdapter.propNoFade`). Such a prop
 * stays whole over a character and still hides it, so it still conceals what stands behind it.
 *
 * @param frame The frame and its flags.
 * @returns False for a `noFade` frame, else true.
 */
export function propFades(frame: FrameSize & { readonly noFade?: true }): boolean {
  return frame.noFade !== true;
}

/**
 * The share of a box's pixels that lie inside a prop's art bounds above its base, the bounds mirrored with
 * the prop's flips; without art bounds the whole frame width above the base stands in. Every solid pixel
 * lies inside the art bounds, so this is never less than `propCoverage` for the same box: a prop whose art
 * holds less than a share of the box cannot hide that share, and its pixels need not be counted.
 *
 * @param prop The prop's anchor and flips.
 * @param size Its frame.
 * @param tileSize Tile size in pixels.
 * @param box The box in world pixels, whole pixels, every edge inclusive.
 * @returns A share in `[0, 1]`.
 * @throws RangeError when the box is empty or an edge is not a whole pixel.
 */
export function propArtOverlap(prop: Placement, size: FrameSize, tileSize: number, box: PixelBox): number {
  const area = boxArea(box);
  return (run(box.x0, box.x1, artLeft(prop, size, tileSize), artWidth(size)) * run(box.y0, box.y1, artTop(prop, size, tileSize), artHeight(size))) / area;
}

/**
 * The share of a box's pixels that lie on a prop's solid pixels, mirrored with the prop's flips. Pixels of
 * the box outside the frame count as uncovered, so the share is of the whole box.
 *
 * @param prop The prop's anchor and flips.
 * @param mask Its frame's solid pixels.
 * @param tileSize Tile size in pixels.
 * @param box The box in world pixels, whole pixels, every edge inclusive.
 * @returns A share in `[0, 1]`.
 * @throws RangeError when the box is empty or an edge is not a whole pixel.
 */
export function propCoverage(prop: Placement, mask: FrameMask, tileSize: number, box: PixelBox): number {
  const area = boxArea(box);
  const left = prop.x * tileSize;
  const top = (prop.y + 1) * tileSize - mask.h;
  const x0 = Math.max(box.x0, Math.ceil(left));
  const x1 = Math.min(box.x1, Math.ceil(left + mask.w) - 1);
  const y0 = Math.max(box.y0, Math.ceil(top));
  const y1 = Math.min(box.y1, Math.ceil(top + mask.h) - 1);
  let covered = 0;
  for (let y = y0; y <= y1; y += 1) {
    const dy = Math.floor(y - top);
    const row = (prop.flipY ? mask.h - 1 - dy : dy) * mask.w;
    for (let x = x0; x <= x1; x += 1) {
      const dx = Math.floor(x - left);
      covered += mask.solid[row + (prop.flipX ? mask.w - 1 - dx : dx)] as number;
    }
  }
  return covered / area;
}

/**
 * Whether the world pixel at a point lies on a prop's solid pixels, mirrored with the prop's flips: the point form of
 * `propCoverage`, for a caller that asks about one place at a time (where rain falls, where a puddle lies) and allocates
 * nothing.
 *
 * @param prop The prop's anchor and flips.
 * @param mask Its frame's solid pixels.
 * @param tileSize Tile size in pixels.
 * @param x World x in pixels; fractions read the pixel they fall in.
 * @param y World y in pixels.
 * @returns True when the point is on a solid pixel; false outside the frame and for a point that is not a number.
 */
export function propSolidAt(prop: Placement, mask: FrameMask, tileSize: number, x: number, y: number): boolean {
  const dx = Math.floor(x - prop.x * tileSize);
  const dy = Math.floor(y - ((prop.y + 1) * tileSize - mask.h));
  if (!(dx >= 0 && dx < mask.w && dy >= 0 && dy < mask.h)) {
    return false;
  }
  const row = (prop.flipY ? mask.h - 1 - dy : dy) * mask.w;
  return mask.solid[row + (prop.flipX ? mask.w - 1 - dx : dx)] === 1;
}

/**
 * Marks the pixels of a box that lie on a prop's solid pixels, mirrored with the prop's flips, in a byte per box
 * pixel the caller keeps, row-major from the box's top-left: several props marked into one set of bytes give the
 * union of what they hide, an overlap counted once, where `propCoverage` answers for one prop alone. The caller
 * clears the bytes before a measurement and divides the marks by the box's area for the share.
 *
 * @param prop The prop's anchor and flips.
 * @param mask Its frame's solid pixels.
 * @param tileSize Tile size in pixels.
 * @param box The box in world pixels, whole pixels, every edge inclusive.
 * @param marks One byte per box pixel, 1 where some prop already hides it; written in place.
 * @returns How many pixels this prop newly marked.
 * @throws RangeError when the box is empty or an edge is not a whole pixel, or `marks` is smaller than the box.
 */
export function markCoverage(prop: Placement, mask: FrameMask, tileSize: number, box: PixelBox, marks: Uint8Array): number {
  const area = boxArea(box);
  if (marks.length < area) {
    throw new RangeError(`marks hold ${marks.length} pixels, fewer than the box's ${area}`);
  }
  const width = box.x1 - box.x0 + 1;
  const left = prop.x * tileSize;
  const top = (prop.y + 1) * tileSize - mask.h;
  const x0 = Math.max(box.x0, Math.ceil(left));
  const x1 = Math.min(box.x1, Math.ceil(left + mask.w) - 1);
  const y0 = Math.max(box.y0, Math.ceil(top));
  const y1 = Math.min(box.y1, Math.ceil(top + mask.h) - 1);
  let marked = 0;
  for (let y = y0; y <= y1; y += 1) {
    const dy = Math.floor(y - top);
    const row = (prop.flipY ? mask.h - 1 - dy : dy) * mask.w;
    const out = (y - box.y0) * width - box.x0;
    for (let x = x0; x <= x1; x += 1) {
      const dx = Math.floor(x - left);
      if (mask.solid[row + (prop.flipX ? mask.w - 1 - dx : dx)] === 1 && marks[out + x] === 0) {
        marks[out + x] = 1;
        marked += 1;
      }
    }
  }
  return marked;
}

/**
 * Whether a prop covers a character (ADR-071 decision 9): it draws above the character, its depth greater
 * than the character's `standingDepth`, and its solid pixels hide at least one pixel of the body box. The
 * editor's stand-in and the game's character are compared by this one test.
 *
 * @param prop The prop's anchor and flips.
 * @param mask Its frame's solid pixels.
 * @param propDepth The depth the prop draws at.
 * @param tileSize Tile size in pixels.
 * @param body The character's body box in world pixels, whole pixels, every edge inclusive.
 * @param standing The character's depth.
 * @returns True when the prop covers the character.
 * @throws RangeError when the prop draws above the character and the box is empty or off whole pixels.
 */
export function coversStanding(prop: Placement, mask: FrameMask, propDepth: number, tileSize: number, body: PixelBox, standing: number): boolean {
  return propDepth > standing && propCoverage(prop, mask, tileSize, body) > 0;
}

/**
 * How many whole pixels of an inclusive range show a run of frame pixels drawn from a world origin.
 *
 * @param from First pixel of the range.
 * @param to Last pixel of the range.
 * @param origin World position of the run's first frame pixel, possibly between pixels.
 * @param length Frame pixels in the run.
 * @returns The count, 0 when they miss.
 */
function run(from: number, to: number, origin: number, length: number): number {
  return Math.max(0, Math.min(to, Math.ceil(origin + length) - 1) - Math.max(from, Math.ceil(origin)) + 1);
}

/**
 * The number of pixels in a box.
 *
 * @param box The box, every edge inclusive.
 * @returns Its area.
 * @throws RangeError when the box is empty or an edge is not a whole pixel.
 */
function boxArea(box: PixelBox): number {
  if (!Number.isInteger(box.x0) || !Number.isInteger(box.y0) || !Number.isInteger(box.x1) || !Number.isInteger(box.y1)) {
    throw new RangeError(`box (${box.x0}, ${box.y0}) to (${box.x1}, ${box.y1}) is not on whole pixels`);
  }
  if (box.x1 < box.x0 || box.y1 < box.y0) {
    throw new RangeError(`box (${box.x0}, ${box.y0}) to (${box.x1}, ${box.y1}) is empty`);
  }
  return (box.x1 - box.x0 + 1) * (box.y1 - box.y0 + 1);
}
